// client/src/components/mischief/Scrawl.js
//
// Caveat-script margin note. Small, tilted, hand-drawn feel.
// Every instance picks its own rotation from a small set so multiple
// Scrawls on the same page don't march in lockstep — that's the
// difference between "designed" and "drawn."
//
// Usage:
//   <Scrawl>game day!</Scrawl>
//   <Scrawl rotate={-3}>← captain</Scrawl>
//   <Scrawl size="lg" color="accent">home game!</Scrawl>
//   <Scrawl strikethrough>lose</Scrawl>

import { useMemo } from 'react';

// A rotation is picked once at mount and never changes — otherwise the
// scrawl would jitter on every re-render. Sampled from a small tilt
// range that never sits flat (0°) and never leans harder than 4°.
const TILTS = [-4, -3, -2, -1, 1, 2, 3, 4];
const pickTilt = () => TILTS[Math.floor(Math.random() * TILTS.length)];

// Size → Tailwind text-utility. Kept as a plain map so it's obvious
// what the sizes are; adding a new one is one line.
const SIZE_CLASS = {
  sm: 'text-base',   // ~16px
  md: 'text-xl',     // ~20px  (default)
  lg: 'text-3xl',    // ~30px  (feels right for Caveat at "lg")
};

// Color → Tailwind text-utility. Only lists colors that make sense
// for handwriting; you shouldn't Scrawl in yellow highlighter.
const COLOR_CLASS = {
  ink:    'text-ink',
  accent: 'text-accent',
  marker: 'text-marker',
  red:    'text-red',
  inkSub: 'text-inkSub',
};

export function Scrawl({
  children,
  rotate,                 // number (degrees) OR undefined for random
  size = 'md',
  color = 'ink',
  strikethrough = false,
  className = '',
}) {
  // useMemo so the random tilt is stable across re-renders of THIS
  // component instance. Different instances still get different tilts
  // because each has its own useMemo.
  const tilt = useMemo(
    () => (typeof rotate === 'number' ? rotate : pickTilt()),
    [rotate]
  );

  return (
    <span
      className={[
        'font-scrawl inline-block leading-none',
        SIZE_CLASS[size] ?? SIZE_CLASS.md,
        COLOR_CLASS[color] ?? COLOR_CLASS.ink,
        strikethrough ? 'line-through decoration-2' : '',
        className,
      ].filter(Boolean).join(' ')}
      style={{ transform: `rotate(${tilt}deg)` }}
    >
      {children}
    </span>
  );
}